import { AbstractControl, ValidationErrors, ValidatorFn } from "@angular/forms";

const MEXICO_LOCAL_PHONE_DIGITS = 10;

function isAllSameDigit(digits: string): boolean {
  return digits.split("").every((c) => c === digits[0]);
}

function isStrictAscending(digits: string): boolean {
  for (let i = 0; i < digits.length - 1; i++) {
    const a = parseInt(digits[i], 10);
    const b = parseInt(digits[i + 1], 10);
    if (b !== a + 1) {
      return false;
    }
  }
  return true;
}

function isStrictDescending(digits: string): boolean {
  for (let i = 0; i < digits.length - 1; i++) {
    const a = parseInt(digits[i], 10);
    const b = parseInt(digits[i + 1], 10);
    if (b !== a - 1) {
      return false;
    }
  }
  return true;
}

/** e.g. 1234567890 (9 → 0) */
function isCyclicAscending(digits: string): boolean {
  for (let i = 0; i < digits.length - 1; i++) {
    const a = parseInt(digits[i], 10);
    const b = parseInt(digits[i + 1], 10);
    if (b !== (a + 1) % 10) {
      return false;
    }
  }
  return true;
}

/** e.g. 0987654321 (0 → 9) */
function isCyclicDescending(digits: string): boolean {
  for (let i = 0; i < digits.length - 1; i++) {
    const a = parseInt(digits[i], 10);
    const b = parseInt(digits[i + 1], 10);
    if (b !== (a - 1 + 10) % 10) {
      return false;
    }
  }
  return true;
}

/**
 * Detects trivial linear phone patterns: all identical digits, or strictly
 * ascending/descending sequences (including wrap at 0/9).
 */
export function isPhoneDigitsLinearPattern(digits: string): boolean {
  if (!digits || digits.length !== MEXICO_LOCAL_PHONE_DIGITS || !/^\d{10}$/.test(digits)) {
    return false;
  }
  if (isAllSameDigit(digits)) {
    return true;
  }
  return (
    isStrictAscending(digits) || isStrictDescending(digits) || isCyclicAscending(digits) || isCyclicDescending(digits)
  );
}

/**
 * Angular validator: rejects 10-digit Mexican local numbers that are linear/repeated.
 * Incomplete values are skipped (handled by required/pattern).
 */
export function phoneLinearPatternValidator(): ValidatorFn {
  return (control: AbstractControl): ValidationErrors | null => {
    const raw = (control.value ?? "").toString();
    const digits = raw.replace(/\D/g, "");
    if (digits.length !== MEXICO_LOCAL_PHONE_DIGITS) {
      return null;
    }
    return isPhoneDigitsLinearPattern(digits) ? { linearPhonePattern: true } : null;
  };
}
